import {WalletTiming} from '../utils/walletPrefetch';

/** ms since page load, the same clock as every event's `dwellMs`. */
export const nowMs = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : 0;

/**
 * The last thing the payment button waited for, i.e. whichever prerequisite finished last:
 * - `render`: everything was ready by the time the paywall rendered, so the button appeared with it.
 * - `wallet_check`: Stripe.js loading plus the Apple/Google Pay check finished last.
 * - `checkout_setup`: identity / `stripe-setup-checkout` finished last (visits without a redeemed person id).
 *
 * Decided from when each prerequisite actually finished, not from how late the event fired, so a slow React commit
 * or a delayed effect doesn't get blamed on checkout.
 */
export type ButtonWaitedFor = 'render' | 'wallet_check' | 'checkout_setup';

/** A prerequisite that finished within this many ms of the paywall rendering counts as ready at render. */
const SLACK_MS = 50;

/**
 * Timing properties for `[Web Payments] Paywall Payment Method Shown`, measured when the event fires.
 *
 * `msAfterPaywallViewed` is the headline: how long the paywall was on screen with no payment button. It is measured on
 * the page's own clock (`performance.now()`), not from Amplitude event times. Those are unreliable this early in a
 * load, because the Segment snippet (4.13.1) stamps queued events when it replays them, not when they fired.
 *
 * `checkoutReadyAt` is when the page first had what it needs to charge (a redeemed person id or a client secret), on
 * the same clock; null if unknown.
 */
export const buttonWaitTiming = (
  paywallViewedAt: number | null,
  wallet: WalletTiming,
  checkoutReadyAt: number | null,
  shownAt: number = nowMs(),
): Record<string, number | boolean | string> => {
  const timing: Record<string, number | boolean | string> = {...wallet};
  if (checkoutReadyAt !== null) {
    timing.checkoutReadyMs = Math.round(checkoutReadyAt);
  }
  if (paywallViewedAt === null) {
    return timing;
  }
  timing.msAfterPaywallViewed = Math.max(0, Math.round(shownAt - paywallViewedAt));

  const walletDone = wallet.walletCheckDoneMs ?? -Infinity;
  const checkoutDone = checkoutReadyAt ?? -Infinity;
  const lastPrerequisite = Math.max(walletDone, checkoutDone);
  let waitedFor: ButtonWaitedFor;
  if (lastPrerequisite <= paywallViewedAt + SLACK_MS) {
    waitedFor = 'render';
  } else if (checkoutDone > walletDone) {
    waitedFor = 'checkout_setup';
  } else {
    waitedFor = 'wallet_check';
  }
  timing.buttonWaitedFor = waitedFor;
  return timing;
};
